import { ApiError, type ProblemDetail } from './problem'
import { oidcUserManager } from '../auth/oidcUserManager'

/**
 * Small typed fetch wrapper shared by every module's API client. Owns:
 *   - the `/api/v1` base path (proxied to the backend in dev — vite.config.ts)
 *   - JSON request/response bodies
 *   - RFC 9457 problem+json error parsing → {@link ApiError}
 *   - the `ETag`/`version` read-back used for optimistic-concurrency (If-Match)
 *   - ADR-0048 (Phase A, #7): the `Authorization: Bearer <token>` injector — every module's API
 *     client inherits this from here, the ONLY place a token is read/attached.
 *
 * Every later module's list/detail screens should reuse this instead of
 * calling `fetch` directly — that is the point of IB-4 being the reference
 * vertical slice on the frontend as well as the backend.
 */

const API_BASE = '/api/v1'

export interface ApiResult<T> {
  data: T
  /** Present when the response carries an `ETag`/version header (GET/POST of a mutable resource). */
  version: number | null
}

export interface RequestOptions {
  signal?: AbortSignal | undefined
  /** Required by the backend's `IdempotencyInterceptor` on every creating POST. */
  idempotencyKey?: string | undefined
  /** Required by `IfMatch.requireVersion` on PATCH/DELETE. */
  ifMatchVersion?: number | undefined
}

/** ADR-0048 (Phase A, #7): reads the current OIDC session (if any) from the shared `oidcUserManager`
 * — the SAME instance `<AuthProvider>` uses — and attaches its access token. Never mints/decodes a
 * token itself; if no session is present (or it has expired and silent renew hasn't caught up yet),
 * the request goes out unauthenticated and the backend's real resource-server chain rejects it
 * (401) exactly as it would for anyone else — {@link request} below reacts to that by redirecting
 * to login, never by retrying with a stale/absent credential silently. */
async function buildHeaders(options: RequestOptions | undefined, hasBody: boolean): Promise<HeadersInit> {
  const headers: Record<string, string> = {}
  if (hasBody) {
    headers['Content-Type'] = 'application/json'
  }
  if (options?.idempotencyKey) {
    headers['Idempotency-Key'] = options.idempotencyKey
  }
  if (options?.ifMatchVersion !== undefined) {
    headers['If-Match'] = String(options.ifMatchVersion)
  }
  const user = await oidcUserManager.getUser()
  if (user?.access_token) {
    headers['Authorization'] = `Bearer ${user.access_token}`
  }
  return headers
}

function parseVersion(res: Response): number | null {
  const etag = res.headers.get('ETag')
  if (!etag) return null
  const bare = etag.replace(/^"|"$/g, '')
  const n = Number(bare)
  return Number.isFinite(n) ? n : null
}

async function toApiError(res: Response): Promise<ApiError> {
  let problem: ProblemDetail
  try {
    const body = (await res.json()) as Partial<ProblemDetail>
    problem = { status: res.status, ...body }
  } catch {
    problem = { status: res.status, title: res.statusText }
  }
  return new ApiError(problem)
}

async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body: unknown,
  options?: RequestOptions,
): Promise<ApiResult<T>> {
  const hasBody = body !== undefined
  const init: RequestInit = {
    method,
    headers: await buildHeaders(options, hasBody),
  }
  if (hasBody) {
    init.body = JSON.stringify(body)
  }
  if (options?.signal) {
    init.signal = options.signal
  }
  const res = await fetch(`${API_BASE}${path}`, init)

  if (res.status === 401) {
    // ADR-0048 (Phase A, #7): no valid/renewable session — start a fresh Authorization-Code+PKCE
    // redirect rather than surfacing a raw 401 the caller has no useful way to recover from. Fires
    // once per rejected request; `signinRedirect` navigates the browser away, so no further code in
    // this call runs.
    void oidcUserManager.signinRedirect()
    throw await toApiError(res)
  }

  if (!res.ok) {
    throw await toApiError(res)
  }

  if (res.status === 204) {
    return { data: undefined as T, version: null }
  }

  const data = (await res.json()) as T
  return { data, version: parseVersion(res) }
}

export const apiClient = {
  get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, undefined, options),
  post: <T>(path: string, body: unknown, options?: RequestOptions) =>
    request<T>('POST', path, body, options),
  put: <T>(path: string, body: unknown, options?: RequestOptions) =>
    request<T>('PUT', path, body, options),
  patch: <T>(path: string, body: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, body, options),
  del: (path: string, options?: RequestOptions) => request<void>('DELETE', path, undefined, options),
}

/** A v4 UUID for the `Idempotency-Key` header — falls back if `crypto.randomUUID` is unavailable. */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `idem-${Date.now()}-${Math.random().toString(16).slice(2)}`
}
